import { useLifecycleHooks } from '@/composables/useLifecycleHooks';
import type { Job, JobFamily, Factor, FactorValue, PayBand, CreateJobInput, FactorPlan, EmploymentChangeModel, NewlyHiredModel, EmployeeCopilotKeyValue, UnAssignedModel, ObservedFields } from '@sysarb/sysarb-types-v2/services/v1/job';
import type { JobsQueryInput } from '@sysarb/sysarb-types-v2/services/v1/job';
import { CareerType } from '@sysarb/sysarb-types-v2/services/v1/job-architecture';

import { defineStore } from 'pinia';
import type { Ref} from 'vue';
import { computed, ref } from 'vue';
import type { TreeView } from '@/views/jobArchitecture/types/JobProfileTable';
import type { ExtendedJobModel } from '@/views/jobArchitecture/types/JobTypes';
import { useDomainStorage } from '@/composables/useDomainStorage';
import { useUser } from '@/login/composables/useUser';

export type TranslatedFactor = {
    translatedTitle: string;
    translatedDescription: string;
} & Factor;

export type TranslatedFactorValue = {
    translatedValue: string;
} & FactorValue;

export enum Tabs {
    'General' = 'General',
    'Employments' = 'Employments',
    'JobDescription' = 'JobDescription',
}

export type QuickFilters = {
    hideIncompleteJobProfiles: boolean;
    onlyProfilesWithEmployments: boolean;
    onlyJobProfilesForRole: boolean;
};

const storeId = 'jobArchitecture';

const storeDef = defineStore(storeId, () => {
    const { isAdministrator, isSupport, isConsultant } = useUser();
    /** State */
    const _factorPlan: Ref<FactorPlan | null> = ref(null);
    const _factorTranslationsMap: Ref<Map<string, TranslatedFactor>> = ref(new Map());
    const _factorValueTranslationsMap: Ref<Map<string, TranslatedFactorValue>> = ref(new Map());
    const _savingDraft = ref(0);
    const _selectedSidebarTab = ref<Tabs>(Tabs.General);

    // Filters
    const _positionFilterValues = useDomainStorage<string[]>('_positionFilterValues', [], sessionStorage);
    const _statisticalCodeFilterValues = useDomainStorage<string[]>('_statisticalCodeFilterValues', [], sessionStorage);
    const _organisationFilterValues = useDomainStorage<(string)[]>('_organisationFilterValues', [], sessionStorage);
    const _jobTitleFilterValues = useDomainStorage<string[]>('_jobTitleFilterValues', [], sessionStorage);
    const _levelFilterValues = useDomainStorage<(string | null)[]>('_levelFilterValues', [], sessionStorage);
    const _levelNumberFilterValues = useDomainStorage<(number | null)[]>('_levelNumberFilterValues', [], sessionStorage);
    const _globalFamiliesFilterValues = useDomainStorage<(string | null)[]>('_globalFamiliesFilterValues', [], sessionStorage);
    const _jobClusterFilterValues = useDomainStorage<(string | null)[]>('_jobClusterFilterValues', [], sessionStorage);
    const _careerTypeFilterValues = useDomainStorage<CareerType[]>('_careerTypeFilterValues', [], sessionStorage);
    const _countryFilterValues = useDomainStorage<string[]>('_countryFilterValues', [], sessionStorage);
    const _statutoryGradeFilterValues = useDomainStorage<string[]>('_statutoryGradeFilterValues', [], sessionStorage);
    const _occupationalClassificationFilterValues = useDomainStorage<string[]>('_occupationalClassificationFilterValues', [], sessionStorage);

    const _copilotNewHiresColumnVisibility = useDomainStorage<Record<string, boolean>>('_copilotNewHiresColumnVisibility', {}, sessionStorage);
    const _copilotSuggestionsColumnVisibility = useDomainStorage<Record<string, boolean>>('_copilotSuggestionsColumnVisibility', {}, sessionStorage);

    const _employmentsGroupByUnassigned = useDomainStorage<string | null>('_employmentsGroupByUnassigned', null, sessionStorage);
    const _employmentsGroupByAll = useDomainStorage<string | null>('_employmentsGroupByAll', null, sessionStorage);

    // Job State
    const _jobProfiles: Ref<Iterable<Job> | null> = ref(null);
    const _filteredJobProfiles: Ref<Iterable<Job> | null> = ref(null);
    const _createdJobs: Ref<Job[]> = ref([]);
    const _modifiedJobs: Ref<Job[]> = ref([]);
    const _deletedJobs: Ref<Job[]> = ref([]);

    // PayBand State
    const _payBands: Ref<Iterable<PayBand> | null> = ref(null);
    const _createdPayBands: Ref<PayBand[]> = ref([]);
    const _modifiedPayBands: Ref<PayBand[]> = ref([]);
    const _deletedPayBands: Ref<PayBand[]> = ref([]);

    const _selectedJobProfile = ref<string | null>(null);
    const _familyCatalog = ref<JobFamily[]>([]);
    const _highestFactorLevelValue = ref<number>(0);
    const _factorLevelFilterValues = ref<(string | null)[]>([]);
    const _isDraftMode = useDomainStorage<boolean>('isDraftMode', true, sessionStorage);
    const _employeeCopilotSettings = ref<ObservedFields | null>(null);
    const _suggestedJobProfileGroups = ref<EmploymentChangeModel | null>(null);
    const _newlyHiredSuggestedJobProfileGroups = ref<NewlyHiredModel | null>(null);
    const _unassignedEmployees = ref<UnAssignedModel | null>(null);
    const _employeeCopilotKeyValuesState = ref<EmployeeCopilotKeyValue[] | null>(null);
    const _quickFilters = useDomainStorage<QuickFilters>('_quickFilters', {
        hideIncompleteJobProfiles: false,
        onlyProfilesWithEmployments: false,
        onlyJobProfilesForRole: false,
    }, sessionStorage);
    const _newJobProfile = useDomainStorage<CreateJobInput>('newJobProfile', {
        code: '',
        title: '',
        comment: '',
        careerType: CareerType.IndividualContributor,
        nodeID: null,
        factors: [],
    }, sessionStorage);

    const _jobArchitectureTreeView = ref<TreeView<ExtendedJobModel> | null>(null);

    /** Getters */
    const selectedSidebarTab = computed(() => _selectedSidebarTab.value);
    const factorPlan = computed(() => _factorPlan.value);
    const savingDraft = computed(() => _savingDraft.value);
    const factorTranslationsMap = computed(() => _factorTranslationsMap.value);
    const factorValueTranslationsMap = computed(() => _factorValueTranslationsMap.value);

    const jobProfiles = computed(() => Array.from(_jobProfiles.value ?? []));
    const jobArchitectureTreeView = computed(() => _jobArchitectureTreeView.value);
    const filteredJobProfiles = computed(() => Array.from(_filteredJobProfiles.value ?? []));

    // Filter values
    const positionFilterValues = computed(() => _positionFilterValues.value);
    const globalFamilyFilterValues = computed(() => _globalFamiliesFilterValues.value);
    const jobTitleFilterValues = computed(() => _jobTitleFilterValues.value);
    const levelFilterValues = computed(() => _levelFilterValues.value);
    const levelNumberFilterValues = computed(() => _levelNumberFilterValues.value);
    const statisticalCodeFilterValues = computed(() => _statisticalCodeFilterValues.value);
    const organisationFilterValues = computed(() => _organisationFilterValues.value);
    const jobClusterFilterValues = computed(() => _jobClusterFilterValues.value);
    const careerTypeFilterValues = computed(() => _careerTypeFilterValues.value);
    const countryFilterValues = computed(() => _countryFilterValues.value);
    const statutoryGradeFilterValues = computed(() => _statutoryGradeFilterValues.value);
    const occupationalClassificationFilterValues = computed(() => _occupationalClassificationFilterValues.value);

    const selectedJobProfile = computed(() => _selectedJobProfile.value);
    const familyCatalog = computed(() => _familyCatalog.value);
    const isDraftMode = computed(() => {
        if (isAdministrator.value || isSupport.value || isConsultant.value) {
            return _isDraftMode.value;
        }
        return false;
    });

    const jobFiltersQuery = computed((): JobsQueryInput => ({
        position: _positionFilterValues.value.length > 0 ? _positionFilterValues.value : undefined,
        statisticalCodeId: _statisticalCodeFilterValues.value.length > 0 ? _statisticalCodeFilterValues.value : undefined,
        organizationNodeId: _organisationFilterValues.value.length > 0 ? _organisationFilterValues.value : undefined,
        jobTitle: _jobTitleFilterValues.value.length > 0 ? _jobTitleFilterValues.value : undefined,
        valuationId: _levelFilterValues.value.length > 0 ? _levelFilterValues.value : undefined,
        globalJobFamilyNodeId: _globalFamiliesFilterValues.value.length > 0 ? _globalFamiliesFilterValues.value : undefined,
        clusterTagID: _jobClusterFilterValues.value.length > 0 ? _jobClusterFilterValues.value : undefined,
        careerType: _careerTypeFilterValues.value.length > 0 ? _careerTypeFilterValues.value : undefined,
        countryCode: _countryFilterValues.value.length > 0 ? _countryFilterValues.value : undefined,
        statutoryGrade: _statutoryGradeFilterValues.value.length > 0 ? _statutoryGradeFilterValues.value : undefined,
        occupationalClassification: _occupationalClassificationFilterValues.value.length > 0 ? _occupationalClassificationFilterValues.value : undefined,
        onlyProfilesWithEmployments: _quickFilters.value.onlyProfilesWithEmployments,
        onlyShowJobsForRole: _quickFilters.value.onlyJobProfilesForRole,
        // There is a reactivity issue with isDraftMode computed so to get around that we do the necessary checks here
        draft: isAdministrator.value || isSupport.value || isConsultant.value ? _isDraftMode.value : false,
    }));

    const jobIdMap = computed<Map<string, Job>>(() => {
        const map = new Map<string, Job>();
        for (const job of filteredJobProfiles.value) {
            map.set(job.id, job);
        }
        return map;
    });

    const activeFilters = computed(() => {
        let activeFilterItems = 0;
        activeFilterItems += _positionFilterValues.value.length;
        activeFilterItems += _statisticalCodeFilterValues.value.length;
        activeFilterItems += _organisationFilterValues.value.length;
        activeFilterItems += _jobTitleFilterValues.value.length;
        activeFilterItems += _levelFilterValues.value.length;
        activeFilterItems += _globalFamiliesFilterValues.value.length;
        activeFilterItems += _jobClusterFilterValues.value.length;
        activeFilterItems += _careerTypeFilterValues.value.length;
        activeFilterItems += _countryFilterValues.value.length;
        activeFilterItems += _statutoryGradeFilterValues.value.length;
        activeFilterItems += _occupationalClassificationFilterValues.value.length;

        activeFilterItems += _quickFilters.value.hideIncompleteJobProfiles ? 1 : 0;
        activeFilterItems += _quickFilters.value.onlyProfilesWithEmployments ? 1 : 0;

        if (!isAdministrator.value && !isSupport.value && !isConsultant.value) {
            activeFilterItems += _quickFilters.value.onlyJobProfilesForRole ? 1 : 0;
        }

        return activeFilterItems;
    });

    const createdJobs = computed(() => _createdJobs.value);
    const modifiedJobs = computed(() => _modifiedJobs.value);
    const deletedJobs = computed(() => _deletedJobs.value);
    const payBands = computed(() => Array.from(_payBands.value ?? []));
    const createdPayBands = computed(() => _createdPayBands.value);
    const modifiedPayBands = computed(() => _modifiedPayBands.value);
    const deletedPayBands = computed(() => _deletedPayBands.value);
    const employeeCopilotSettings = computed(() => _employeeCopilotSettings.value);
    const suggestedJobProfileGroups = computed(() => _suggestedJobProfileGroups.value);
    const newlyHiredSuggestedJobProfileGroups = computed(() => _newlyHiredSuggestedJobProfileGroups.value);
    const unassignedEmployees = computed(() => _unassignedEmployees.value);
    const employeeCopilotKeyValuesState = computed(() => _employeeCopilotKeyValuesState.value);
    const quickFilters = computed(() => _quickFilters.value);
    const factorLevelFilterValues = computed(() => _factorLevelFilterValues.value);
    const highestFactorLevelValue = computed(() => _highestFactorLevelValue.value);
    const newJobProfile = computed(() => _newJobProfile.value);
    const employmentsGroupByUnassigned = computed(() => _employmentsGroupByUnassigned.value);
    const employmentsGroupByAll = computed(() => _employmentsGroupByAll.value);
    const copilotNewHiresColumnVisibility = computed(() => _copilotNewHiresColumnVisibility.value);
    const copilotSuggestionsColumnVisibility = computed(() => _copilotSuggestionsColumnVisibility.value);

    /** Actions */

    function setFactorPlan(factorPlan: FactorPlan | null): void {
        _factorPlan.value = factorPlan;
    }

    function setJobProfiles(jobs: Iterable<Job> | null): void {
        _jobProfiles.value = jobs;
    }

    function setPayBands(payBands: Iterable<PayBand> | null): void {
        _payBands.value = payBands;
    }

    function setFilteredJobProfiles(jobs: Iterable<Job> | null): void {
        _filteredJobProfiles.value = jobs;
    }

    function setFactorTranslationsMap(map: Map<string, TranslatedFactor>): void {
        _factorTranslationsMap.value = map;
    }

    function setFactorValueTranslationsMap(map: Map<string, TranslatedFactorValue>): void {
        _factorValueTranslationsMap.value = map;
    }

    function setPositionsFilterValues(values: string[]): void {
        _positionFilterValues.value = values;
    }

    function setGlobalFamilyFilterValues(values: (string | null)[]): void {
        _globalFamiliesFilterValues.value = values;
    }

    function setJobTitleFilterValues(values: string[]): void {
        _jobTitleFilterValues.value = values;
    }

    function setLevelFilterValues(values: (string | null)[]): void {
        _levelFilterValues.value = values;
    }

    function setLevelNumberFilterValues(values: (number | null)[]): void {
        _levelNumberFilterValues.value = values;
    }

    function setStatisticalCodeFilterValues(values: string[]): void {
        _statisticalCodeFilterValues.value = values;
    }

    function setOrganisationFilterValues(values: string[]): void {
        _organisationFilterValues.value = values;
    }

    function setFactorLevelFilterValues(values: (string | null)[]): void {
        _factorLevelFilterValues.value = values;
    }

    function setJobClusterFilterValues(values: (string | null)[]): void {
        _jobClusterFilterValues.value = values;
    }

    function setCareerTypeFilterValues(values: CareerType[]): void {
        _careerTypeFilterValues.value = values;
    }

    function setCountryFilterValues(values: string[]): void {
        _countryFilterValues.value = values;
    }

    function setStatutoryGradeFilterValues(values: string[]): void {
        _statutoryGradeFilterValues.value = values;
    }

    function setOccupationalClassificationFilterValues(values: string[]): void {
        _occupationalClassificationFilterValues.value = values;
    }

    function setEmploymentsGroupByUnassigned(value: string | null): void {
        _employmentsGroupByUnassigned.value = value;
    }

    function setEmploymentsGroupByAll(value: string | null): void {
        _employmentsGroupByAll.value = value;
    }

    function setCopilotNewHiresColumnVisibility(value: Record<string, boolean>): void {
        _copilotNewHiresColumnVisibility.value = value;
    }

    function setCopilotSuggestionsColumnVisibility(value: Record<string, boolean>): void {
        _copilotSuggestionsColumnVisibility.value = value;
    }

    function setSelectedJobProfile(jobProfileId: string | null): void {
        _selectedJobProfile.value = jobProfileId;
    }

    function setFamilyCatalog(families: JobFamily[]): void {
        _familyCatalog.value = families;
    }

    function setCreatedJobs(jobs: Job[]): void {
        _createdJobs.value = jobs;
    }

    function setModifiedJobs(jobs: Job[]): void {
        _modifiedJobs.value = jobs;
    }

    function setDeletedJobs(jobs: Job[]): void {
        _deletedJobs.value = jobs;
    }

    function setCreatedPayBands(payBands: PayBand[]): void {
        _createdPayBands.value = payBands;
    }

    function setModifiedPayBands(payBands: PayBand[]): void {
        _modifiedPayBands.value = payBands;
    }

    function setDeletedPayBands(payBands: PayBand[]): void {
        _deletedPayBands.value = payBands;
    }

    function setEmployeeCopilotSettings(settings: ObservedFields): void {
        _employeeCopilotSettings.value = settings;
    }

    function setSuggestedJobProfileGroups(groups: EmploymentChangeModel): void {
        _suggestedJobProfileGroups.value = groups;
    }

    function setNewlyHiredSuggestedJobProfileGroups(groups: NewlyHiredModel): void {
        _newlyHiredSuggestedJobProfileGroups.value = groups;
    }

    function setUnassignedEmployees(employees: UnAssignedModel): void {
        _unassignedEmployees.value = employees;
    }

    function setEmployeeCopilotKeyValues(groups: EmployeeCopilotKeyValue[]): void {
        _employeeCopilotKeyValuesState.value = groups;
    }

    function setHighestFactorLevelValue(value: number): void {
        _highestFactorLevelValue.value = value;
    }

    function setQuickFilters(value: QuickFilters): void {
        _quickFilters.value = value;
    }

    /** Update */

    function setJobArchitectureTreeView(tree: TreeView<ExtendedJobModel> | null): void {
        _jobArchitectureTreeView.value = tree;
    }

    function updatePositionFilterValues(filterValue: string): void {
        if (_positionFilterValues.value.includes(filterValue)) {
            _positionFilterValues.value = _positionFilterValues.value.filter(value => value !== filterValue);
        } else {
            _positionFilterValues.value.push(filterValue);
        }
    }

    function updateJobTitleFilterValues(filterValue: string): void {
        if (_jobTitleFilterValues.value.includes(filterValue)) {
            _jobTitleFilterValues.value = _jobTitleFilterValues.value.filter(value => value !== filterValue);
        } else {
            _jobTitleFilterValues.value.push(filterValue);
        }
    }

    function updateLevelFilterValues(filterValue: string | null): void {
        if (_levelFilterValues.value.includes(filterValue)) {
            _levelFilterValues.value = _levelFilterValues.value.filter(value => value !== filterValue);
        } else {
            _levelFilterValues.value.push(filterValue);
        }
    }

    function updateLevelNumberFilterValues(filterValue: number | null): void {
        if (_levelNumberFilterValues.value.includes(filterValue)) {
            _levelNumberFilterValues.value = _levelNumberFilterValues.value.filter(value => value !== filterValue);
        } else {
            _levelNumberFilterValues.value.push(filterValue);
        }
    }

    function updateStatisticalCodeValues(filterValue: string): void {
        if (_statisticalCodeFilterValues.value.includes(filterValue)) {
            _statisticalCodeFilterValues.value = _statisticalCodeFilterValues.value.filter(value => value !== filterValue);
        } else {
            _statisticalCodeFilterValues.value.push(filterValue);
        }
    }

    function updateOrganisationValues(filterValue: (string | null)[]): void {
        _organisationFilterValues.value = filterValue.notEmpty();
    }

    function updateGlobalFamilyValues(filterValue: string | null): void {
        if (_globalFamiliesFilterValues.value.includes(filterValue)) {
            _globalFamiliesFilterValues.value = _globalFamiliesFilterValues.value.filter(value => value !== filterValue);
        } else {
            _globalFamiliesFilterValues.value.push(filterValue);
        }
    }

    function updateFactorLevelValues(filterValue: string | null): void {
        if (_factorLevelFilterValues.value.includes(filterValue)) {
            _factorLevelFilterValues.value = _factorLevelFilterValues.value.filter(value => value !== filterValue);
        } else {
            _factorLevelFilterValues.value.push(filterValue);
        }
    }

    function updateJobClusterValues(filterValue: string | null): void {
        if (_jobClusterFilterValues.value.includes(filterValue)) {
            _jobClusterFilterValues.value = _jobClusterFilterValues.value.filter(value => value !== filterValue);
        } else {
            _jobClusterFilterValues.value.push(filterValue);
        }
    }

    function updateCareerTypeValues(filterValue: CareerType): void {
        if (_careerTypeFilterValues.value.includes(filterValue)) {
            _careerTypeFilterValues.value = _careerTypeFilterValues.value.filter(value => value !== filterValue);
        } else {
            _careerTypeFilterValues.value.push(filterValue);
        }
    }

    function updateCountryValues(filterValue: string): void {
        if (_countryFilterValues.value.includes(filterValue)) {
            _countryFilterValues.value = _countryFilterValues.value.filter(value => value !== filterValue);
        } else {
            _countryFilterValues.value.push(filterValue);
        }
    }

    function updateStatutoryGradeValues(filterValue: string): void {
        if (_statutoryGradeFilterValues.value.includes(filterValue)) {
            _statutoryGradeFilterValues.value = _statutoryGradeFilterValues.value.filter(value => value !== filterValue);
        } else {
            _statutoryGradeFilterValues.value.push(filterValue);
        }
    }

    function updateOccupationalClassificationValues(filterValue: string): void {
        if (_occupationalClassificationFilterValues.value.includes(filterValue)) {
            _occupationalClassificationFilterValues.value = _occupationalClassificationFilterValues.value.filter(value => value !== filterValue);
        } else {
            _occupationalClassificationFilterValues.value.push(filterValue);
        }
    }

    function updateNewJobProfile<T extends keyof CreateJobInput>(key: T, value: CreateJobInput[T]): void {
        _newJobProfile.value[key] = value;
    }

    function updateDraftMode(value: boolean): void {
        _isDraftMode.value = value;
    }

    function toggleDraftMode(): void {
        _isDraftMode.value = !_isDraftMode.value;
    }

    function updateQuickFilters<T extends keyof QuickFilters>(key: T, value: QuickFilters[T]): void {
        _quickFilters.value[key] = value;
    }

    function selectSideBarTab(tab: Tabs): void {
        _selectedSidebarTab.value = tab;
    }

    function startSavingDraft(): void {
        _savingDraft.value++;

        // This timeout is used to simulate the time it takes to save the draft
        setTimeout(() => {
            _savingDraft.value--;
        }, Math.floor(Math.random() * (3000 - 1000 + 1)) + 1000);
    }

    function resetQuickFilters(): void {
        _quickFilters.value = {
            hideIncompleteJobProfiles: false,
            onlyProfilesWithEmployments: false,
            onlyJobProfilesForRole: false,
        };
    }

    function resetPositionFilterValues(): void {
        _positionFilterValues.value = [];
    }

    function resetJobTitleFilterValues(): void {
        _jobTitleFilterValues.value = [];
    }

    function resetLevelFilterValues(): void {
        _levelFilterValues.value = [];
    }

    function resetLevelNumberFilterValues(): void {
        _levelNumberFilterValues.value = [];
    }

    function resetStatisticalCodeValues(): void {
        _statisticalCodeFilterValues.value = [];
    }

    function resetOrganisationValues(): void {
        _organisationFilterValues.value = [];
    }

    function resetGlobalFamilyValues(): void {
        _globalFamiliesFilterValues.value = [];
    }

    function resetFactorLevelValues(): void {
        _factorLevelFilterValues.value = [];
    }

    function resetJobClusterValues(): void {
        _jobClusterFilterValues.value = [];
    }

    function resetCareerTypeValues(): void {
        _careerTypeFilterValues.value = [];
    }

    function resetCountryValues(): void {
        _countryFilterValues.value = [];
    }

    function resetStatutoryGradeValues(): void {
        _statutoryGradeFilterValues.value = [];
    }

    function resetOccupationalClassificationValues(): void {
        _occupationalClassificationFilterValues.value = [];
    }

    function resetAllFilters(): void {
        resetPositionFilterValues();
        resetJobTitleFilterValues();
        resetLevelFilterValues();
        resetLevelNumberFilterValues();
        resetStatisticalCodeValues();
        resetOrganisationValues();
        resetGlobalFamilyValues();
        resetFactorLevelValues();
        resetJobClusterValues();
        resetCareerTypeValues();
        resetCountryValues();
        resetStatutoryGradeValues();
        resetOccupationalClassificationValues();
        resetQuickFilters();
    }

    function resetNewJobProfile(): void {
        _newJobProfile.value = {
            code: '',
            title: '',
            comment: '',
            careerType: CareerType.IndividualContributor,
            nodeID: null,
            factors: [],
        };
    }

    function resetStoreState(): void {
        _positionFilterValues.value = [];
        _globalFamiliesFilterValues.value = [];
        _jobTitleFilterValues.value = [];
        _levelFilterValues.value = [];
        _statisticalCodeFilterValues.value = [];
        _organisationFilterValues.value = [];
        _selectedJobProfile.value = null;
        _familyCatalog.value = [];
        _isDraftMode.value = true;
        _createdJobs.value = [];
        _modifiedJobs.value = [];
        _deletedJobs.value = [];
        _employeeCopilotSettings.value = null;
        _suggestedJobProfileGroups.value = null;
        _newlyHiredSuggestedJobProfileGroups.value = null;
        _unassignedEmployees.value = null;
        _employeeCopilotKeyValuesState.value = [];
        _highestFactorLevelValue.value = 0;
        _factorLevelFilterValues.value = [];
        _jobClusterFilterValues.value = [];
        _careerTypeFilterValues.value = [];
        _countryFilterValues.value = [];
        _statutoryGradeFilterValues.value = [];
        _occupationalClassificationFilterValues.value = [];
        _newJobProfile.value = {
            code: '',
            title: '',
            comment: '',
            careerType: CareerType.IndividualContributor,
            nodeID: null,
            factors: [],
        };
    }

    return {
        /** Getters */
        factorPlan,
        payBands,
        jobArchitectureTreeView,
        jobProfiles,
        filteredJobProfiles,
        savingDraft,
        factorTranslationsMap,
        factorValueTranslationsMap,
        positionFilterValues,
        globalFamilyFilterValues,
        jobTitleFilterValues,
        levelFilterValues,
        levelNumberFilterValues,
        statisticalCodeFilterValues,
        organisationFilterValues,
        factorLevelFilterValues,
        jobClusterFilterValues,
        careerTypeFilterValues,
        countryFilterValues,
        statutoryGradeFilterValues,
        occupationalClassificationFilterValues,
        copilotNewHiresColumnVisibility,
        copilotSuggestionsColumnVisibility,
        selectedJobProfile,
        familyCatalog,
        employeeCopilotSettings,
        suggestedJobProfileGroups,
        newlyHiredSuggestedJobProfileGroups,
        unassignedEmployees,
        employeeCopilotKeyValuesState,
        quickFilters,
        isDraftMode,
        selectedSidebarTab,

        createdJobs,
        modifiedJobs,
        deletedJobs,

        createdPayBands,
        modifiedPayBands,
        deletedPayBands,

        highestFactorLevelValue,
        newJobProfile,
        employmentsGroupByUnassigned,
        employmentsGroupByAll,
        jobFiltersQuery,
        activeFilters,
        jobIdMap,

        /** Actions */
        toggleDraftMode,
        updateQuickFilters,
        updateDraftMode,
        startSavingDraft,

        /** Set */
        selectSideBarTab,
        setFactorPlan,
        setJobProfiles,
        setPayBands,
        setFilteredJobProfiles,
        setFactorTranslationsMap,
        setFactorValueTranslationsMap,
        setPositionsFilterValues,
        setGlobalFamilyFilterValues,
        setJobTitleFilterValues,
        setLevelFilterValues,
        setLevelNumberFilterValues,
        setStatisticalCodeFilterValues,
        setOrganisationFilterValues,
        setFactorLevelFilterValues,
        setJobClusterFilterValues,
        setCareerTypeFilterValues,
        setCountryFilterValues,
        setStatutoryGradeFilterValues,
        setOccupationalClassificationFilterValues,
        setEmploymentsGroupByUnassigned,
        setEmploymentsGroupByAll,
        setSelectedJobProfile,
        setFamilyCatalog,
        setCreatedJobs,
        setModifiedJobs,
        setDeletedJobs,
        setCreatedPayBands,
        setModifiedPayBands,
        setDeletedPayBands,
        setEmployeeCopilotSettings,
        setSuggestedJobProfileGroups,
        setNewlyHiredSuggestedJobProfileGroups,
        setUnassignedEmployees,
        setEmployeeCopilotKeyValues,
        setHighestFactorLevelValue,
        setQuickFilters,
        setJobArchitectureTreeView,
        setCopilotNewHiresColumnVisibility,
        setCopilotSuggestionsColumnVisibility,

        /** Update */
        updateNewJobProfile,
        updatePositionFilterValues,
        updateJobTitleFilterValues,
        updateLevelFilterValues,
        updateLevelNumberFilterValues,
        updateStatisticalCodeValues,
        updateOrganisationValues,
        updateGlobalFamilyValues,
        updateFactorLevelValues,
        updateJobClusterValues,
        updateCareerTypeValues,
        updateCountryValues,
        updateStatutoryGradeValues,
        updateOccupationalClassificationValues,
        /** Reset */
        resetPositionFilterValues,
        resetJobTitleFilterValues,
        resetLevelFilterValues,
        resetLevelNumberFilterValues,
        resetStatisticalCodeValues,
        resetOrganisationValues,
        resetGlobalFamilyValues,
        resetFactorLevelValues,
        resetJobClusterValues,
        resetCareerTypeValues,
        resetCountryValues,
        resetStatutoryGradeValues,
        resetOccupationalClassificationValues,
        resetAllFilters,
        resetStoreState,
        resetNewJobProfile,
        resetQuickFilters
    };
}, {
    onDeactivated() {
        this.resetStoreState();
    },
});

const useJobArchitectureStore = (): ReturnType<typeof storeDef> => useLifecycleHooks(storeDef());

export { useJobArchitectureStore };
